// ** React Imports
import React, { useRef, useEffect, useState } from 'react';

// ** CoreUI Imports
import { CButton, CModal, CModalBody, CForm } from '@coreui/react';
import CIcon from '@coreui/icons-react';

// ** Validations
import { Formik } from 'formik';
import * as Yup from 'yup';

// ** Constants Imports
import DateRangeConfig from '../../report-view/dialog/DateRangeConfig';
import { CLIENT_GET_SAVED_REPORT_BY_ID } from '../../../../../../constants';
import { callTokenApi } from '../../../../../../apiCaller';

const validationSchema = (isSaveReport) => {
     return Yup.object().shape({
          dateRangeConfig: isSaveReport
               ? Yup.object()
                      .nullable()
                      .shape({
                           rolling: Yup.object()
                                .nullable()
                                .shape({
                                     numberPrevious: Yup.number()
                                          .nullable()
                                          .when('type', {
                                               is: (type) => ['previous_x_days', 'previous_x_weeks'].includes(type),
                                               then: (schema) => schema.required('Field Required').min(1, 'Must be greater than 0'),
                                               otherwise: (schema) => schema.notRequired(),
                                          }),
                                }),
                      })
               : Yup.mixed().notRequired(),
     });
};

const OverwriteReport = ({ show, data, dateRangeOverride, setDateRangeOverride, onCancel, dataSources }) => {
     const { descriptionOverride, reportViewId } = data;
     const reportName = descriptionOverride ? JSON.parse(descriptionOverride).reportName : '';
     const initialValues = {};
     const [saveReport, setSaveReport] = useState({});

     let determinedConfig = saveReport?.content?.dateRangeConfig;

     if (dateRangeOverride) {
          const overrideData = dateRangeOverride;
          const reportOverride = Array.isArray(overrideData) ? overrideData.find((item) => item.reportViewId === reportViewId) : undefined;

          if (reportOverride?.dateRangeConfig) {
               determinedConfig = reportOverride.dateRangeConfig;
          }
     }
     if (determinedConfig) {
          initialValues['dateRangeConfig'] = determinedConfig;
     }

     const nameInputRef = useRef();

     useEffect(() => {
          if (show) {
               setTimeout(() => {
                    nameInputRef.current?.focus();
               }, 0);
               const query = `?id=${reportViewId}`;
               callTokenApi(`${CLIENT_GET_SAVED_REPORT_BY_ID}/${query}`, 'GET').then((response) => {
                    if (response.status === 200) {
                         const reportView = response.data.reportViews;
                         if (reportView) {
                              setSaveReport(reportView);
                         }
                    }
               });
          }
     }, [show, reportViewId]);

     const onSubmit = (values) => {
          let currentOverrides = dateRangeOverride?.filter(item => dataSources.some(dataSource => dataSource.reportViewId === item.reportViewId)) || [];
         
          const indexToUpdate = currentOverrides.findIndex((data) => data.reportViewId === reportViewId);

          const newItemData = {
               reportViewId,
               ...values,
          };

          let updatedOverrides;

          if (indexToUpdate !== -1) {
               updatedOverrides = currentOverrides.map((item, index) => (index === indexToUpdate ? newItemData : item));
          } else {
               updatedOverrides = [...currentOverrides, newItemData];
          }
          setDateRangeOverride(updatedOverrides);
          onCancel();
     };

     return (
          <div className="confirm-save-popup select-domain popup-create-variable savedview">
               <CModal visible={show} onClose={onCancel} color="primary" alignment="center" portal={false}>
                    <CModalBody style={{ padding: '38px 0 30px' }} className="text-center confirm-save">
                         <CIcon icon="cil-x" onClick={onCancel} className="icon-close-popup"></CIcon>

                         <div className="popup-publish-version-description">
                              <h1> Overwrite and Report Dates</h1>
                              <p>
                                   Modify the date range that the {reportName} uses for this scheduled analysis. This will not change your Saved
                                   Report, it will only apply to this specific schedule for this specific AI Insights Template.
                              </p>
                         </div>

                         <Formik
                              initialValues={initialValues}
                              validationSchema={validationSchema(true)}
                              onSubmit={onSubmit}
                              onClose={onCancel}
                              validateOnChange={false}
                              validateOnBlur={false}
                              enableReinitialize
                         >
                              {({ values, errors, handleSubmit, setFieldValue, setFieldError }) => {
                                   return (
                                        <CForm onSubmit={handleSubmit} noValidate>
                                             <DateRangeConfig
                                                  values={values}
                                                  errors={errors}
                                                  setFieldValue={setFieldValue}
                                                  setFieldError={setFieldError}
                                                  reportName={reportName}
                                                  data={data}
                                                  disableDateRangeOptions={true}
                                             />

                                             <div className="form-actions text-center mt-4">
                                                  <CButton type="submit" className="btn-primary">
                                                       Save
                                                  </CButton>

                                                  <CButton className="btn btn-cancel rounded-pill cancel" onClick={onCancel}>
                                                       Cancel
                                                  </CButton>
                                             </div>
                                        </CForm>
                                   );
                              }}
                         </Formik>
                    </CModalBody>
               </CModal>
          </div>
     );
};

export default OverwriteReport;
